import styled from '@emotion/styled';
import type { StylesConfig } from 'src/components/DropdownWrapper/DropdownWrapper';
import { dropdownWrapper } from 'src/assets/colors';

export const getAbsoluteTextPosition = (hasInternalLabel: boolean): {
    position: 'absolute';
    top: string;
    left: string;
    fontWeight: number;
    fontSize: string;
    lineHeight: string;
    pointerEvents: 'none';
} => ({
    position: 'absolute' as const,
    top: hasInternalLabel ? '20px' : '13px',
    left: '12px',
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: '22px',
    pointerEvents: 'none' as const,
});

export const getDefaultStyles = (hasInternalLabel: boolean): StylesConfig<unknown, boolean> => {
    const absoluteTextPosition = getAbsoluteTextPosition(hasInternalLabel);

    return {
    control: (base) => ({
        ...base,
        minHeight: '48px',
        height: '48px',
        border: `1px solid ${dropdownWrapper.control.borderColor}`,
        borderRadius: '4px',
        boxShadow: 'none',
        backgroundColor: dropdownWrapper.control.bgColor,
        cursor: 'pointer',
        fontSize: '16px',
        fontFamily: 'inherit',
        color: dropdownWrapper.control.txtColor,
        '&:hover': {
            borderColor: dropdownWrapper.control.borderColor,
        },
    }),
    valueContainer: (base) => ({
        ...base,
        padding: '0',
        height: '48px',
        display: 'block',
        position: 'relative',
    }),
    singleValue: (base) => ({
        ...base,
        ...absoluteTextPosition,
        transform: 'none',
        margin: 0,
        color: dropdownWrapper.control.txtColor,
        maxWidth: 'calc(100% - 30px)',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    }),
    placeholder: (base) => ({
        ...base,
        ...absoluteTextPosition,
        color: dropdownWrapper.placeholder.txtColor,
    }),
    indicatorsContainer: (base) => ({
        ...base,
        position: 'absolute',
        right: '14px',
        top: '50%',
        transform: 'translateY(-50%)',
        height: 'auto',
    }),
    input: (base) => ({
        ...base,
        margin: 0,
        padding: 0,
        fontSize: '16px',
        color: dropdownWrapper.control.txtColor,
        width: '100%',
        boxSizing: 'border-box',
    }),
    menu: (base) => ({
        ...base,
        margin: 0,
        position: 'static',
        boxShadow: 'none',
        border: 'none',
        borderRadius: 0,
    }),
    menuList: (base) => ({
        ...base,
        padding: 0,
        margin: 0,
        maxHeight: '260px',
        overflowY: 'auto',
    }),
    option: (base) => ({
        ...base,
        padding: 0,
        backgroundColor: 'transparent',
        color: 'inherit',
        cursor: 'default',
        ':active': {
            backgroundColor: 'transparent',
        },
    }),
    noOptionsMessage: (base) => ({
        ...base,
        padding: '8px 12px',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: 400,
        textAlign: 'center',
        color: dropdownWrapper.noOptions.txtColor,
    }),
    dropdownIndicator: (base) => ({
        ...base,
        padding: 0,
        color: 'transparent',
    }),
    indicatorSeparator: () => ({
        display: 'none',
    }),
    clearIndicator: () => ({
        display: 'none',
    }),
    };
};

export const DropdownSelectWrapper = styled('div', { label: 'DropdownSelectWrapper' })`
    position: relative;
    width: 100%;
`;

export const DropdownIndicatorWrapper = styled('div', { label: 'DropdownIndicatorWrapper' })`
    position: relative;
    width: 20px;
    height: 20px;
`;

export const DropdownIndicatorArrow = styled('span', { label: 'DropdownIndicatorArrow' })<{ position: 'left' | 'right' }>`
    background-color: ${dropdownWrapper.indicator.bgColor};
    border-radius: 2px;
    content: '';
    display: block;
    height: 10px;
    position: absolute;
    right: 0;
    width: 4px;
    top: 50%;
    transform: ${({ position }): string =>
        position === 'right'
            ? 'translateY(-50%) translateX(50%) rotate(45deg)'
            : 'translateY(-50%) translateX(-50%) rotate(-45deg)'};
`;

export const MenuOuterWrapper = styled('div', { label: 'MenuOuterWrapper' })`
    border-bottom-right-radius: 4px;
    border-bottom-left-radius: 4px;
    background-color: ${dropdownWrapper.menu.bgColor};
    border: 1px solid ${dropdownWrapper.menu.borderColor};
    border-top-color: ${dropdownWrapper.control.borderColorLight};
    box-shadow: 0 1px 0 ${dropdownWrapper.menu.shadowColor};
    box-sizing: border-box;
    margin-top: -1px;
    max-height: 260px;
    position: absolute;
    left: 0;
    top: 100%;
    width: 100%;
    z-index: 1;
`;

export const MenuListWrapper = styled('div', { label: 'MenuListWrapper' })`
    -webkit-overflow-scrolling: touch;
    scrollbar-color: ${dropdownWrapper.scrollbar.thumbColor} ${dropdownWrapper.scrollbar.trackColor};
    scrollbar-gutter: stable;
    scrollbar-width: thin;

    &::-webkit-scrollbar {
        width: 8px;
    }

    &::-webkit-scrollbar-track {
        background: ${dropdownWrapper.scrollbar.trackColor};
    }

    &::-webkit-scrollbar-thumb {
        background-color: ${dropdownWrapper.scrollbar.thumbColor};
        border-radius: 4px;
    }
`;

export const OptionWrapper = styled('div', { label: 'OptionWrapper' })<{ isSelected: boolean; isFocused: boolean }>`
    background-color: ${({ isSelected, isFocused }): string =>
        isSelected
            ? dropdownWrapper.option.bgColorSelected
            : isFocused
              ? dropdownWrapper.option.bgColorFocused
              : dropdownWrapper.option.bgColor};
    color: ${({ isSelected, isFocused }): string =>
        isSelected || isFocused ? dropdownWrapper.option.txtColorSelected : dropdownWrapper.option.txtColor};
    cursor: pointer;
    display: block;
    min-height: 16px;
    padding: 2px 8px;
    border-bottom: 1px solid ${dropdownWrapper.option.borderColor};
    font-weight: 400;
    white-space: nowrap;
    vertical-align: middle;

    &:hover {
        background-color: ${dropdownWrapper.option.bgColorHover};
    }

    span {
        align-items: center;
        display: flex;
    }
`;

export const DeleteButton = styled('button', { label: 'DeleteButton' })`
    float: right;
    margin-left: 10px;
    padding: 2px 5px;
    background: ${dropdownWrapper.deleteButton.bgColor};
    border: 1px solid ${dropdownWrapper.deleteButton.borderColor};
    cursor: pointer;
    border-radius: 2px;
    font-size: 12px;
    line-height: 1;

    &:hover {
        background: ${dropdownWrapper.deleteButton.bgColorHover};
        color: ${dropdownWrapper.deleteButton.txtColor};
        border-color: ${dropdownWrapper.deleteButton.bgColorHover};
    }
`;
